
    import {nextTick} from 'vue';
    import {Inject, Options, Ref, Vue, Watch} from 'vue-property-decorator';
    import TooltipInfo from '@/brief-hub/common/ui/components/TooltipInfo.vue';
    import TextareaCounter from '@/brief-hub/common/ui/components/TextareaCounter.vue';
    import {Getter} from 'vuex-class';
    import type {
        BriefMissionDescription as MissionDescription,
        BriefMissionTitle as MissionTitle,
        Payload,
    } from '@/brief-hub/store/modules/brief/types';
    import {MutationsType} from '@/brief-hub/store/modules/brief/mutations';
    import InputText from '@malt/joy-entrypoint/src/vue/InputText.vue';
    import TooltipsDescription from '@/brief-hub/deposit/mission/TooltipsDescription.vue';
    import TooltipsTitle from '@/brief-hub/deposit/mission/TooltipsTitle.vue';
    import UserResource from '@/brief-hub/user/UserResource';
    import {BriefTrackingService} from '@/brief-hub/brief/BriefTrackingService';
    import {DI} from '@/brief-hub/config/DependencyInjection';
    import BriefHubStepLayout from '@/brief-hub/common/ui/layout/BriefHubStepLayout.vue';

    interface TooltipsComponent {
        pos: number;
    }

    @Options({
        components: {
            TooltipsDescription,
            TooltipsTitle,
            TextareaCounter,
            BriefHubStepLayout,
            TooltipInfo,
            InputText,
        },
    })
    export default class BriefMissionDescription extends Vue {
        @Inject({from: DI.briefTrackingService})
        briefTrackingService!: BriefTrackingService;

        @Getter('briefMissionTitle')
        briefMissionTitle!: MissionTitle;

        @Getter('briefMissionDescription')
        briefMissionDescription!: MissionDescription;

        @Getter('connectedUser')
        connectedUser!: UserResource | null;

        @Ref('inputMissionTitle')
        readonly inputMissionTitle!: Vue;

        @Ref('inputMissionDescription')
        readonly inputMissionDescription!: any;

        @Ref('missionDetails')
        readonly missionDetails!: HTMLElement;

        private titleTopPosition = 0;
        private descriptionTopPosition = 0;

        private missionDescription: string | null = null;
        private missionTitle: string | null = null;

        private descriptionMaxLength = 10000;
        private descriptionMinLength = 200;
        private titleMaxLength = 120;
        private titleMinLength = 10;

        private isMounted = false;

        private currentTooltips: string | null = null;

        get descriptionPlaceholder(): string {
            const connectedUser = this.connectedUser;

            if (connectedUser) {
                const currentCompany = connectedUser.clientIdentities.find((item) => connectedUser.companyId === item.companyId);
                if (currentCompany) {
                    return this.$msg(
                        'customer.brief.form.mission.description.client.placeholder',
                        connectedUser.firstName,
                        currentCompany.companyName,
                    );
                }
            }

            return this.$msg('customer.brief.form.mission.description.placeholder');
        }

        get descriptionIsValid(): boolean {
            if (!this.missionDescription?.length) {
                return false;
            }
            return this.missionDescription.length >= this.descriptionMinLength && this.missionDescription.length <= this.descriptionMaxLength;
        }

        get titleIsValid(): boolean {
            if (!this.missionTitle?.length) {
                return false;
            }
            return this.missionTitle.length >= this.titleMinLength && this.missionTitle?.length <= this.titleMaxLength;
        }

        handleTextareaChange(event: CustomEvent<{value: string}>) {
            const textarea = event.target as HTMLTextAreaElement;
            this.missionDescription = textarea.value;
        }

        missionTitleInputTopPosition(): number {
            const $input = this.inputMissionTitle.$refs.input as HTMLInputElement;
            const {top} = $input.getBoundingClientRect();
            const inputHeight = $input.offsetHeight;
            return top + inputHeight / 2;
        }

        missionDescriptionInputTopPosition(): Promise<number> {
            const {top} = this.inputMissionDescription.getBoundingClientRect();
            return this.inputMissionDescription.getInputElement().then((textarea: HTMLTextAreaElement) => top + textarea.scrollHeight / 2);
        }

        get currentTooltipsComponentParams(): TooltipsComponent | null {
            if (!this.isMounted) {
                return null;
            }
            const bodyRectTop = document.body.getBoundingClientRect().top;
            const missionDetailsTopPosition = this.missionDetails?.getBoundingClientRect().top - bodyRectTop || 0;
            if (this.currentTooltips === 'title') {
                return {
                    pos: this.titleTopPosition - missionDetailsTopPosition,
                };
            } else if (this.currentTooltips === 'description') {
                return {
                    pos: this.descriptionTopPosition - missionDetailsTopPosition,
                };
            }
            return null;
        }

        get currentTooltipsComponent(): typeof TooltipsTitle | typeof TooltipsDescription | null {
            if (this.currentTooltips === 'title') {
                return TooltipsTitle;
            }
            if (this.currentTooltips === 'description') {
                return TooltipsDescription;
            }
            return null;
        }

        async created(): Promise<void> {
            this.missionDescription = this.briefMissionDescription.description || '';
            this.missionTitle = this.briefMissionTitle.title || '';
            this.briefTrackingService?.triggerBriefFunnelStepEnteredAmplitudeEvent('mission_description', 5, this.$router.currentRoute.value);
        }

        async mounted() {
            await nextTick();
            await nextTick();
            this.titleTopPosition = this.missionTitleInputTopPosition();
            this.descriptionTopPosition = await this.missionDescriptionInputTopPosition();
            this.isMounted = true;
        }

        @Watch('missionDescription')
        @Watch('missionTitle')
        onFormChange(): void {
            let payload: Payload | null = null;
            if (this.descriptionIsValid && this.titleIsValid) {
                payload = {
                    type: 'MissionDescriptionBriefRequest',
                    payload: {
                        title: this.missionTitle!,
                        description: this.missionDescription!,
                    },
                };
            }

            this.$store.commit(MutationsType.UPDATE_PAYLOAD, payload);
        }
    }
